import { BankNote01, BookOpen02, Lightbulb02, ShieldTick, ShoppingCart01, SlashCircle01, Wallet02 } from "@untitledui/icons";
import {
    CommissionTimeline,
    ExplainAmount,
    GLOSSARY,
    type GlossaryTerm,
    IconChip,
    PageHeader,
    PageSection,
    REASONS,
    type ReasonCode,
    ReasonBadge,
    explainCommission,
} from "@mfa-aff/ui";
import step1 from "@mfa-aff/ui/assets/illustrations/how-step-1.webp";
import step2 from "@mfa-aff/ui/assets/illustrations/how-step-2.webp";
import step3 from "@mfa-aff/ui/assets/illustrations/how-step-3.webp";
import step4 from "@mfa-aff/ui/assets/illustrations/how-step-4.webp";

const DAY = 86_400_000;
const now = Date.now();
const sampleTimeline = {
    orderedAt: new Date(now - 6 * DAY),
    paidAt: new Date(now - 6 * DAY),
    eligibleAt: new Date(now + 9 * DAY),
};
const example = explainCommission({ baseRial: 34_000_000n, rateBp: 2200, discountBp: 800, levelName: "نقره‌ای", rateLabel: "کد تخفیف" });

const steps = [
    {
        title: "لینک یا کد را به اشتراک بگذارید",
        description: "لینک اختصاصی، کد معرف یا کد تخفیفی که خودتان ساخته‌اید.",
        image: step1,
    },
    {
        title: "مشتری خرید می‌کند",
        description: "سفارش به نام شما ثبت می‌شود و پورسانت از روی فاکتور پرداخت‌شده حساب می‌شود.",
        image: step2,
    },
    {
        title: "۱۴ روز ضمانت",
        description: "اگر مشتری در این مدت وجهش را پس نگیرد و سرویس فعال بماند، پورسانت قطعی می‌شود.",
        image: step3,
    },
    {
        title: "کیف پول یا شبا",
        description: "برداشت به کیف پول میزبان‌فا با ۵٪ هدیه، یا واریز به حساب بانکی به نام خودتان.",
        image: step4,
    },
];

const attributionOrder = [
    { title: "تمدید سرویسی که قبلاً از طرف شما خریده شده", body: "تمدیدها همیشه به بازاریابی می‌رسد که سرویس را آورده است." },
    { title: "کد تخفیف شما", body: "اگر مشتری کد تخفیف شما را وارد کند، سفارش مال شماست؛ حتی اگر لینک کس دیگری را باز کرده باشد." },
    { title: "کد معرف شما", body: "کدی که مشتری در فیلد «کد معرف» صفحهٔ پرداخت می‌نویسد." },
    { title: "آخرین لینکی که مشتری باز کرده", body: "لینک شما تا ۹۰ روز در مرورگر مشتری ذخیره می‌ماند." },
    { title: "مشتری متصل به شما", body: "مشتری‌ای که قبلاً از طرف شما خرید کرده تا ۱۲ ماه به شما متصل می‌ماند." },
];

const noCommission: ReasonCode[] = ["DOMAIN_ONLY", "SELF_REFERRAL", "REFUNDED_IN_GUARANTEE", "SERVICE_NOT_ACTIVE", "NO_SIGNAL"];

const glossaryOrder: GlossaryTerm[] = [
    "pending",
    "available",
    "paidOut",
    "guarantee",
    "activeService",
    "level",
    "codePool",
    "renewalRate",
    "referralCode",
    "partnerPromo",
    "attribution",
    "clawback",
    "sheba",
];

const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
    <section className={`flex flex-col gap-4 rounded-2xl bg-primary p-5 shadow-xs ring-1 ring-secondary ring-inset md:p-6 ${className}`}>{children}</section>
);

export const HowItWorksPage = () => (
    <>
        <PageHeader icon={Lightbulb02} title="چطور کار می‌کند" description="از معرفی تا واریز، در چهار قدم؛ با جواب سؤال‌هایی که معمولاً پیش می‌آید." />

        <PageSection>
            <section
                className="relative isolate overflow-hidden rounded-2xl bg-brand-gradient p-5 text-white shadow-lg ring-1 ring-white/10 ring-inset md:p-8"
                aria-labelledby="steps-title"
            >
                <div aria-hidden="true" className="absolute -bottom-24 -start-20 -z-10 size-80 rounded-full bg-accent-500/20 blur-3xl" />
                <h2 id="steps-title" className="mb-5 text-lg font-semibold text-white md:mb-6">
                    از معرفی تا واریز، در چهار قدم
                </h2>
                <ol className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {steps.map((step, i) => (
                        <li key={step.title} className="flex gap-4 rounded-xl bg-white/8 p-4 ring-1 ring-white/12 ring-inset sm:flex-col sm:gap-3">
                            <img src={step.image} alt="" aria-hidden="true" draggable={false} className="size-20 shrink-0 object-contain drop-shadow-lg select-none sm:mx-auto sm:size-28" />
                            <div className="flex min-w-0 flex-col gap-1.5">
                                <span className="flex items-center gap-2">
                                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-accent-500 text-xs font-semibold text-white">{i + 1}</span>
                                    <h3 className="text-md font-semibold text-white">{step.title}</h3>
                                </span>
                                <p className="text-sm leading-6 text-white/75">{step.description}</p>
                            </div>
                        </li>
                    ))}
                </ol>
            </section>
        </PageSection>

        <PageSection>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
                <Card>
                    <div className="flex items-center gap-3">
                        <IconChip icon={ShoppingCart01} color="brand" />
                        <h2 className="text-lg font-semibold text-primary">سفارش چطور به نام شما ثبت می‌شود؟</h2>
                    </div>
                    <p className="text-sm text-tertiary">
                        برای هر سفارش، این فهرست از بالا بررسی می‌شود و اولین موردی که برقرار باشد برنده است. دلیل تصمیم کنار هر سفارش در صفحهٔ «سفارش‌ها» نوشته می‌شود.
                    </p>
                    <ol className="flex flex-col gap-3">
                        {attributionOrder.map((item, i) => (
                            <li key={item.title} className="flex gap-3">
                                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-secondary text-xs font-semibold text-brand-secondary">
                                    {i + 1}
                                </span>
                                <div className="flex flex-col gap-0.5">
                                    <p className="text-sm font-semibold text-secondary">{item.title}</p>
                                    <p className="text-sm text-tertiary">{item.body}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </Card>

                <Card>
                    <div className="flex items-center gap-3">
                        <IconChip icon={BankNote01} color="accent" />
                        <h2 className="text-lg font-semibold text-primary">پورسانت چطور حساب می‌شود؟</h2>
                    </div>
                    <p className="text-sm text-tertiary">
                        مبنای پورسانت، مبلغ هر آیتم فاکتور بدون مالیات و تخفیف است. اگر با کد تخفیف خودتان به مشتری تخفیف بدهید، تخفیف را میزبان‌فا می‌دهد و پورسانت شما از مبلغی حساب می‌شود که مشتری پس از تخفیف پرداخت کرده.
                    </p>
                    <div className="flex flex-col gap-3 rounded-xl bg-secondary p-4">
                        <p className="text-sm font-medium text-secondary">نمونه: هاست سالانه با کد تخفیف ۸٪، سطح نقره‌ای</p>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-sm text-tertiary">پورسانت شما (روی مبلغ بزنید)</span>
                            <ExplainAmount rial={example.resultRial} lines={example.lines} moneyProps={{ size: "lg", className: "font-semibold text-success-primary dark:text-accent-300" }} />
                        </div>
                        <p className="text-xs leading-5 text-tertiary">{example.sentence}</p>
                    </div>
                    <p className="text-sm text-tertiary">
                        تمدیدها هم پورسانت دارند: هر بار که مشتری سرویسش را تمدید کند، نرخ تمدید سطح شما روی همان تمدید حساب می‌شود.
                    </p>
                </Card>

                <Card>
                    <div className="flex items-center gap-3">
                        <IconChip icon={ShieldTick} color="warning" />
                        <h2 className="text-lg font-semibold text-primary">چرا ۱۴ روز صبر می‌کنیم؟</h2>
                    </div>
                    <p className="text-sm text-tertiary">
                        مشتری ممکن است در روزهای اول سفارشش را لغو کند و مبلغ را پس بگیرد. برای همین پورسانت هر فروش جدید ۱۴ روز «در انتظار» می‌ماند و بعد از آن، اگر سرویس فعال
                        مانده باشد، خودکار قابل برداشت می‌شود. برای تمدید، این دوره ۷ روز است.
                    </p>
                    <div className="rounded-xl bg-secondary p-4">
                        <CommissionTimeline data={sampleTimeline} />
                    </div>
                </Card>

                <Card>
                    <div className="flex items-center gap-3">
                        <IconChip icon={Wallet02} color="blue" />
                        <h2 className="text-lg font-semibold text-primary">پول چطور به شما می‌رسد؟</h2>
                    </div>
                    <ul className="flex flex-col gap-3 text-sm">
                        <li className="flex flex-col gap-0.5">
                            <span className="font-semibold text-secondary">کیف پول میزبان‌فا، با ۵٪ هدیه</span>
                            <span className="text-tertiary">فوری و بدون حداقل بالا؛ برای تمدید سرویس‌های خودتان یا خرید تازه.</span>
                        </li>
                        <li className="flex flex-col gap-0.5">
                            <span className="font-semibold text-secondary">حساب بانکی (شبا)</span>
                            <span className="text-tertiary">فقط به شبای هم‌نام شما؛ شمارهٔ پیگیری بانک روی رسید ثبت می‌شود.</span>
                        </li>
                        <li className="flex flex-col gap-0.5">
                            <span className="font-semibold text-secondary">تسویهٔ خودکار</span>
                            <span className="text-tertiary">می‌توانید بگویید هر وقت موجودی به مبلغ دلخواهتان رسید، خودکار برداشت شود.</span>
                        </li>
                    </ul>
                    <p className="text-sm text-tertiary">
                        اگر بعد از قطعی شدن پورسانت، مشتری وجهش را پس بگیرد، به همان نسبت از پورسانت‌های بعدی کم می‌شود. هرگز از شما پول نقد خواسته نمی‌شود.
                    </p>
                </Card>
            </div>
        </PageSection>

        <PageSection>
            <Card>
                <div className="flex items-center gap-3">
                    <IconChip icon={SlashCircle01} color="error" />
                    <h2 className="text-lg font-semibold text-primary">چه سفارش‌هایی پورسانت ندارند؟</h2>
                </div>
                <ul className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {noCommission.map((code) => (
                        <li key={code} className="flex flex-col items-start gap-2 rounded-xl bg-secondary p-4">
                            <ReasonBadge code={code} explain={false} />
                            <p className="text-sm text-secondary">{REASONS[code].why}</p>
                            <p className="text-sm text-tertiary">{REASONS[code].next}</p>
                        </li>
                    ))}
                </ul>
            </Card>
        </PageSection>

        <PageSection>
            <Card>
                <div className="flex items-center gap-3">
                    <IconChip icon={BookOpen02} color="gray" />
                    <h2 className="text-lg font-semibold text-primary">واژه‌نامه</h2>
                </div>
                <dl className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
                    {glossaryOrder.map((key) => (
                        <div key={key} className="flex flex-col gap-1 border-s-2 border-brand ps-4 dark:border-brand-400">
                            <dt className="text-sm font-semibold text-primary">{GLOSSARY[key].term}</dt>
                            <dd className="text-sm text-tertiary">{GLOSSARY[key].definition}</dd>
                        </div>
                    ))}
                </dl>
            </Card>
        </PageSection>
    </>
);
